import type {
  AxiosBaseQueryArgs,
  AxiosBaseQueryError,
} from '@gasbi/shared/common/utils/axiosBaseQuery';
import type {
  BaseQueryFn,
  EndpointBuilder,
  EndpointDefinitions,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

interface BmsSliceExtraOptions {
  /** The slice's url prefix, put in front of every endpoint url. */
  baseUrl?: string;
}

const bmsBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError,
  BmsSliceExtraOptions
> = (args, api, { baseUrl = '' } = {}) =>
  axiosBaseQuery({ baseUrl })(args, api, {});

/**
 * The single RTK Query API of the BMS UI's own features (GSB-7788). Each createApi adds a
 * middleware, and on the first action every middleware dispatches `middlewareRegistered`
 * through the whole chain again, so the stack grows with the square of their count: with 138
 * of them the first request (GET /api/user) overflowed the stack and the app stayed on its
 * loading screen. Features inject their endpoints here with injectBmsSlice instead.
 */
export const bmsApi = createApi({
  reducerPath: 'bmsApi',
  baseQuery: bmsBaseQuery,
  endpoints: () => ({}),
});

interface BmsSliceOptions<
  Definitions extends EndpointDefinitions,
  TagTypes extends string,
> {
  /** Url prefix of the slice's endpoints, the baseUrl its own createApi used to get. */
  baseUrl: string;
  tagTypes?: readonly TagTypes[];
  /** Default cache lifetime of the slice's queries; an endpoint's own value wins. */
  keepUnusedDataFor?: number;
  endpoints: (
    build: EndpointBuilder<
      typeof bmsBaseQuery,
      TagTypes,
      typeof bmsApi.reducerPath
    >,
  ) => Definitions;
}

/**
 * Injects a feature's endpoints into bmsApi, taking the options its own createApi used to take.
 * Endpoint names share one namespace across the app, so a duplicate name throws at startup
 * instead of silently keeping the first definition.
 */
export const injectBmsSlice = <
  Definitions extends EndpointDefinitions,
  TagTypes extends string = never,
>({
  baseUrl,
  tagTypes = [],
  keepUnusedDataFor,
  endpoints,
}: BmsSliceOptions<Definitions, TagTypes>) =>
  bmsApi.enhanceEndpoints({ addTagTypes: tagTypes }).injectEndpoints({
    endpoints: build =>
      Object.fromEntries(
        Object.entries(endpoints(build)).map(([name, definition]) => [
          name,
          {
            keepUnusedDataFor,
            ...definition,
            extraOptions: {
              baseUrl,
              ...(definition.extraOptions as BmsSliceExtraOptions | undefined),
            },
          },
        ]),
      ) as Definitions,
    overrideExisting: 'throw',
  });
